import { apiFetch } from "@/lib/api/client"
import type {
  AmendInvoiceIn,
  CreateInvoiceIn,
  DealPrefillOut,
  DealSearchOut,
  DealSearchParams,
  EmailPreviewOut,
  InstallmentDefaultsOut,
  InvoiceDetailOut,
  InvoiceFilterFacetsOut,
  InvoiceFilterParams,
  InvoiceListResponse,
  ManualRetryOut,
  NamedDiscountListResponse,
  UpdateInvoiceIn,
  VoidInvoiceIn,
} from "./types"

function buildQueryString(params: object): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === "") continue
        qs.append(key, String(item))
      }
      continue
    }
    qs.set(key, String(value))
  }
  const s = qs.toString()
  return s ? `?${s}` : ""
}

export function listInvoices(
  params: InvoiceFilterParams = {},
  init?: RequestInit,
): Promise<InvoiceListResponse> {
  return apiFetch<InvoiceListResponse>(
    `/api/invoices/${buildQueryString(params)}`,
    init,
  )
}

export function fetchInvoice(
  id: string,
  init?: RequestInit,
): Promise<InvoiceDetailOut> {
  return apiFetch<InvoiceDetailOut>(
    `/api/invoices/${encodeURIComponent(id)}/`,
    init,
  )
}

export function createInvoice(
  body: CreateInvoiceIn,
  init?: RequestInit,
): Promise<InvoiceDetailOut> {
  return apiFetch<InvoiceDetailOut>("/api/invoices/", {
    ...init,
    method: "POST",
    body: JSON.stringify(body),
  })
}

export function updateInvoice(
  id: string,
  body: UpdateInvoiceIn,
  init?: RequestInit,
): Promise<InvoiceDetailOut> {
  return apiFetch<InvoiceDetailOut>(
    `/api/invoices/${encodeURIComponent(id)}/`,
    {
      ...init,
      method: "PATCH",
      body: JSON.stringify(body),
    },
  )
}

export function sendInvoice(
  id: string,
  init?: RequestInit,
): Promise<InvoiceDetailOut> {
  return apiFetch<InvoiceDetailOut>(
    `/api/invoices/${encodeURIComponent(id)}/send/`,
    { ...init, method: "POST", body: JSON.stringify({}) },
  )
}

export function finalizeInvoice(
  id: string,
  init?: RequestInit,
): Promise<InvoiceDetailOut> {
  // Finalize the draft into `created` status without emailing the
  // customer (create-invoice-without-sending-email §6.1). No body — the
  // invoice content was already persisted by the draft create/update
  // step, exactly like `sendInvoice`.
  return apiFetch<InvoiceDetailOut>(
    `/api/invoices/${encodeURIComponent(id)}/finalize/`,
    { ...init, method: "POST", body: JSON.stringify({}) },
  )
}

export function fetchEmailPreview(
  id: string,
  init?: RequestInit,
): Promise<EmailPreviewOut> {
  return apiFetch<EmailPreviewOut>(
    `/api/invoices/${encodeURIComponent(id)}/email-preview/`,
    init,
  )
}


export function voidInvoice(
  id: string,
  body: VoidInvoiceIn,
  init?: RequestInit,
): Promise<InvoiceDetailOut> {
  return apiFetch<InvoiceDetailOut>(
    `/api/invoices/${encodeURIComponent(id)}/void/`,
    { ...init, method: "POST", body: JSON.stringify(body) },
  )
}

export function deleteInvoice(
  id: string,
  init?: RequestInit,
): Promise<void> {
  // Self-service draft delete. The backend returns 204 No Content, which
  // ``apiFetch`` resolves as ``undefined`` — nothing to unwrap.
  return apiFetch<void>(`/api/invoices/${encodeURIComponent(id)}/`, {
    ...init,
    method: "DELETE",
  })
}

export function resendInvoice(
  id: string,
  stage?: "deposit" | "balance",
  init?: RequestInit,
): Promise<InvoiceDetailOut> {
  // Deposit invoices require a ``stage`` query param to pick the leg;
  // full/installment invoices ignore it (the backend leaves it unused).
  const qs = stage ? `?stage=${encodeURIComponent(stage)}` : ""
  return apiFetch<InvoiceDetailOut>(
    `/api/invoices/${encodeURIComponent(id)}/resend/${qs}`,
    { ...init, method: "POST", body: JSON.stringify({}) },
  )
}

export function amendInvoice(
  id: string,
  body: AmendInvoiceIn,
  init?: RequestInit,
): Promise<InvoiceDetailOut> {
  return apiFetch<InvoiceDetailOut>(
    `/api/invoices/${encodeURIComponent(id)}/amend/`,
    { ...init, method: "POST", body: JSON.stringify(body) },
  )
}

export function searchDeals(
  params: DealSearchParams,
  init?: RequestInit,
): Promise<DealSearchOut> {
  return apiFetch<DealSearchOut>(
    `/api/hubspot/deals/search/${buildQueryString(params)}`,
    init,
  )
}

export function manualRetryInstallment(
  invoiceId: string,
  scheduleId: string,
  init?: RequestInit,
): Promise<ManualRetryOut> {
  return apiFetch<ManualRetryOut>(
    `/api/invoices/${encodeURIComponent(invoiceId)}/installments/${encodeURIComponent(scheduleId)}/retry/`,
    { ...init, method: "POST", body: JSON.stringify({}) },
  )
}

export function fetchDealPrefill(
  dealId: string,
  init?: RequestInit,
): Promise<DealPrefillOut> {
  return apiFetch<DealPrefillOut>(
    `/api/hubspot/deals/${encodeURIComponent(dealId)}/prefill/`,
    init,
  )
}

export function getNamedDiscounts(
  init?: RequestInit,
): Promise<NamedDiscountListResponse> {
  return apiFetch<NamedDiscountListResponse>("/api/named-discounts/", init)
}

/**
 * Fetch the market / currency values present in the caller's visible
 * invoices, which back the two list-page filter pickers.
 */
export function getInvoiceFilterFacets(
  init?: RequestInit,
): Promise<InvoiceFilterFacetsOut> {
  return apiFetch<InvoiceFilterFacetsOut>("/api/invoices/filter-facets/", init)
}

export function getInstallmentDefaults(
  init?: RequestInit,
): Promise<InstallmentDefaultsOut> {
  return apiFetch<InstallmentDefaultsOut>(
    "/api/invoices/installment-defaults/",
    init,
  )
}
